import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useEnrollment } from '@/hooks/useEnrollment';
import ProgressRing from '@/components/ProgressRing';
import SessionCard from '@/components/SessionCard';
import StatusBadge from '@/components/StatusBadge';
import Logo from '@/components/Logo';
import { apiClient } from '@/lib/api-client';
import { API_ENDPOINTS } from '@/types/api';

/**
 * Registration form shown before enrollment when user has no tokens.
 */
/** Generate a cryptographically random password (never shown to the user). */
function generateRandomPassword(): string {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return Array.from(array, (b) => b.toString(36).padStart(2, '0')).join('').slice(0, 48);
}

function RegistrationForm({ onRegistered }: { onRegistered: () => void }) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);

    try {
      const response = await apiClient.post<{
        tokens: { access_token: string; refresh_token: string; expires_in: number; token_type: string };
        user: unknown;
      }>(API_ENDPOINTS.AUTH_REGISTER, { full_name: name, email, password: generateRandomPassword() });

      if (response.data.tokens) {
        const t = response.data.tokens;
        apiClient.setTokens({
          accessToken: t.access_token,
          refreshToken: t.refresh_token,
          expiresIn: t.expires_in,
          tokenType: t.token_type,
        });
      }
      onRegistered();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Falha no registro. Tente novamente.');
    } finally {
      setIsSubmitting(false);
    }
  }, [name, email, onRegistered]);

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-6 py-12">
      <div className="text-center mb-8 animate-fade-in">
        <div className="flex items-center justify-center mb-4">
          <Logo markSize="h-9" wordClass="text-lg" />
        </div>
        <h1 className="text-2xl font-bold text-txt-primary font-sans mb-2">
          Criar Conta
        </h1>
        <p className="text-sm text-txt-muted font-sans">
          Registre-se para iniciar o cadastro biometrico
        </p>
      </div>

      <form onSubmit={handleSubmit} className="w-full max-w-sm space-y-4">
        <div>
          <label className="block text-xs text-txt-muted font-sans mb-1">Nome</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Seu nome completo"
            required
            className="w-full px-4 py-3 bg-bg-secondary border border-bg-surface rounded-lg text-txt-primary font-sans text-sm focus:outline-none focus:border-accent-brand transition-colors"
          />
        </div>
        <div>
          <label className="block text-xs text-txt-muted font-sans mb-1">E-mail</label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="seu@email.com"
            required
            className="w-full px-4 py-3 bg-bg-secondary border border-bg-surface rounded-lg text-txt-primary font-sans text-sm focus:outline-none focus:border-accent-brand transition-colors"
          />
        </div>
        {error && (
          <p className="text-sm text-accent-danger font-sans">{error}</p>
        )}

        <button
          type="submit"
          disabled={isSubmitting}
          className="w-full bg-accent-brand hover:bg-accent-brand/90 text-white py-3 rounded-lg font-sans font-semibold text-sm transition-all hover:shadow-lg hover:shadow-accent-brand/20 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSubmitting ? 'Registrando...' : 'Registrar e Continuar'}
        </button>
      </form>
    </div>
  );
}

/**
 * EnrollmentHub — Overview page showing enrollment progress with ProgressRing,
 * SessionCards for completed sessions, and CTA to start next session.
 * Shows a registration form first if the user has no tokens.
 */
export default function EnrollmentHub() {
  const navigate = useNavigate();
  const [isRegistered, setIsRegistered] = useState(() => apiClient.hasTokens());
  const {
    enrollment,
    isLoading,
    error,
    startEnrollment,
    finalizeEnrollment,
  } = useEnrollment();

  // Auto-start enrollment if registered and not started
  useEffect(() => {
    if (isRegistered && enrollment.status === 'not_started') {
      startEnrollment();
    }
  }, [isRegistered, enrollment.status, startEnrollment]);

  const handleRegistered = useCallback(() => {
    setIsRegistered(true);
  }, []);

  const handleStartSession = useCallback(() => {
    navigate('/typing-session');
  }, [navigate]);

  const handleFinalize = useCallback(async () => {
    const result = await finalizeEnrollment();
    if (result?.success) {
      navigate('/profile-created', { state: { result } });
    }
  }, [finalizeEnrollment, navigate]);

  // Show registration form if user has no tokens
  if (!isRegistered) {
    return <RegistrationForm onRegistered={handleRegistered} />;
  }

  const progress =
    (enrollment.completedSessions / enrollment.requiredSessions) * 100;

  const statusVariant = enrollment.profileReady
    ? 'success'
    : enrollment.status === 'in_progress'
      ? 'brand'
      : 'neutral';

  const statusLabel = enrollment.profileReady
    ? 'Pronto para finalizar'
    : enrollment.status === 'in_progress'
      ? `${enrollment.completedSessions}/${enrollment.requiredSessions} sessoes`
      : 'Iniciando...';

  return (
    <div className="min-h-screen flex flex-col items-center px-6 py-12">
      {/* Header */}
      <div className="text-center mb-8 animate-fade-in">
        <h1 className="text-2xl md:text-3xl font-bold text-txt-primary font-sans mb-2">
          Cadastro Biometrico
        </h1>
        <p className="text-sm text-txt-muted font-sans">
          Complete {enrollment.requiredSessions} sessoes de digitacao para criar seu perfil
        </p>
        <div className="mt-3">
          <StatusBadge label={statusLabel} variant={statusVariant} pulse={enrollment.status === 'collecting'} />
        </div>
      </div>

      {/* Progress Ring */}
      <div className="mb-10 animate-slide-up">
        <ProgressRing
          value={progress}
          size={160}
          strokeWidth={10}
          color={enrollment.profileReady ? '#00d26a' : '#7c5cfc'}
          label={`${enrollment.completedSessions} de ${enrollment.requiredSessions}`}
        />
      </div>

      {/* Quality score */}
      {enrollment.completedSessions > 0 && (
        <div className="text-center mb-8 animate-fade-in">
          <span className="text-xs text-txt-muted font-sans block mb-1">Qualidade geral</span>
          <span className="text-3xl font-bold font-mono text-accent-brand">
            {Math.round(enrollment.overallQuality)}%
          </span>
        </div>
      )}

      {/* Session Cards */}
      <div className="w-full max-w-2xl grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-10">
        {enrollment.sessions.map((session, i) => (
          <SessionCard
            key={session.id}
            session={session}
            isActive={i === enrollment.currentSessionIndex && !enrollment.profileReady}
          />
        ))}
      </div>

      {/* Error */}
      {error && (
        <div className="mb-6 p-4 bg-accent-danger/10 border border-accent-danger/30 rounded-card text-sm text-accent-danger font-sans max-w-md text-center">
          {error}
        </div>
      )}

      {/* CTA */}
      <div className="flex flex-col items-center gap-3">
        {!enrollment.profileReady ? (
          <button
            onClick={handleStartSession}
            disabled={isLoading || enrollment.status === 'not_started'}
            className="bg-accent-brand hover:bg-accent-brand/90 text-white px-8 py-3.5 rounded-xl font-sans font-semibold text-base transition-all hover:shadow-lg hover:shadow-accent-brand/20 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {enrollment.completedSessions === 0
              ? 'Iniciar Primeira Sessao'
              : `Iniciar Sessao ${enrollment.currentSessionIndex + 1}`}
          </button>
        ) : (
          <button
            onClick={handleFinalize}
            disabled={isLoading}
            className="bg-accent-success hover:bg-accent-success/90 text-white px-8 py-3.5 rounded-xl font-sans font-semibold text-base transition-all hover:shadow-lg hover:shadow-accent-success/20 disabled:opacity-50"
          >
            {isLoading ? 'Finalizando...' : 'Criar Meu Perfil'}
          </button>
        )}

        <button
          onClick={() => navigate('/')}
          className="text-sm text-txt-muted hover:text-txt-primary transition-colors font-sans"
        >
          Voltar ao inicio
        </button>
      </div>
    </div>
  );
}
